import { useHandler } from "@redotech/react-util/hook";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { FlowType } from "@redotech/redo-model/return-flow";
import { AI_RETURN_REASONS_STEP_TYPE_NAME } from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import { MULTIPLE_CHOICE_STEP_TYPE_NAME } from "@redotech/redo-return-ui/flows/return-flow/condition-types/multiple-choice";
import { MultipleChoiceState } from "@redotech/redo-return-ui/flows/return-flow/multiple-choice";
import {
  Step,
  StepId,
  StepType,
  TypedStep,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { FormSidePanel } from "@redotech/redo-web/form-builder/form-side-panel";
import CheckIcon from "@redotech/redo-web/icon-old/check-thin.svg";
import EditPencilIcon from "@redotech/redo-web/icon-old/edit-pencil.svg";
import { Text } from "@redotech/redo-web/text";
import { memo, SetStateAction, useEffect, useState } from "react";
import { removeStepConnection } from "../utils/update-step-connection";
import * as styles from "./right-panel.module.css";

export const RightPanel = memo(function RightPanel({
  show,
  step,
  stepId,
  flowType,
  steps,
  stepOptions,
  onClose,
  onDelete,
  onStepChange,
  connectionMode,
  startConnectionMode,
}: {
  show: boolean;
  step: Step | null;
  stepId: StepId | undefined;
  flowType: FlowType;
  steps: Map<StepId, Step>;
  stepOptions: StepType<any, any>[];
  onClose: () => void;
  onDelete: (stepId: StepId) => void;
  onStepChange: (
    stepId: StepId,
    newStep: SetStateAction<Step | undefined>,
  ) => void;
  connectionMode: { sourceStepId: string; handleIndex: number } | null;
  startConnectionMode: (sourceStepId: string, handleIndex: number) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState<string>("");

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setTimeout(() => setVisible(true), 10);
    } else if (!show && shouldRender) {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [show, shouldRender]);

  const stepConnectionUI = useHandler(
    ({
      value,
      handleIndex,
    }: {
      value: StepId | undefined | null;
      handleIndex?: number;
    }) => {
      let actualHandleIndex = handleIndex ?? 0;
      if (handleIndex === undefined && step && stepId) {
        const downstreams = step.downstream();
        const foundIndex = downstreams.findIndex((d) => d.id === value);
        if (foundIndex !== -1) {
          actualHandleIndex = foundIndex;
        }
      }

      const connectedStep = value ? steps.get(value) : undefined;
      const isInConnectionMode =
        connectionMode?.sourceStepId === stepId &&
        connectionMode?.handleIndex === actualHandleIndex;

      return (
        <Flex className={styles.connectionContainer} dir="column" gap="sm">
          {connectedStep ? (
            <div className={styles.stepCard}>
              <RedoFeaturedIcon
                color="warning"
                Icon={connectedStep.Icon}
                size="xs"
              />
              <div className={styles.stepCardText}>
                <Text fontSize="xs" fontWeight="semibold">
                  {connectedStep.title()}
                </Text>
                {connectedStep.description(undefined, flowType) && (
                  <Text
                    className={styles.stepCardSubtitle}
                    fontSize="xs"
                    textColor="secondary"
                  >
                    {connectedStep.description(undefined, flowType)}
                  </Text>
                )}
              </div>
            </div>
          ) : (
            <Text fontSize="xs" textColor="primary">
              No next step
            </Text>
          )}

          <RedoButton
            className={
              connectedStep
                ? styles.connectionButtonFloating
                : styles.connectionButton
            }
            hierarchy={connectedStep ? "link_underline" : "secondary"}
            onClick={() => {
              if (stepId) {
                startConnectionMode(stepId, actualHandleIndex);
              }
            }}
            size="xs"
            text={connectedStep ? "Change" : "Add"}
          />

          {isInConnectionMode && (
            <Text fontSize="xs" fontStyle="italic" textColor="tertiary">
              Click on a step to connect
            </Text>
          )}
        </Flex>
      );
    },
  );

  // Step state management
  const setStep = useHandler((newStep: SetStateAction<Step | undefined>) => {
    if (!step || !stepId) {
      return;
    }

    if (typeof newStep === "function") {
      const updatedStep = newStep(step);
      if (updatedStep) {
        onStepChange(stepId, updatedStep);
      }
    } else if (newStep) {
      onStepChange(stepId, newStep);
    }
  });
  // Handle title changes
  const handleTitleChange = useHandler((newTitle: string) => {
    step?.setCustomTitle(newTitle, setStep);
  });

  const handleDeleteClick = useHandler(() => {
    if (stepId && stepId !== "start") {
      onDelete(stepId);
    }
  });

  const handleStepTypeChange = useHandler((newStepType: StepType<any, any>) => {
    if (!step || !stepId) {
      return;
    }

    if (newStepType.title === step.type.title) {
      return;
    }

    const isReturnReasonToMultipleChoice =
      step.type.title !== AI_RETURN_REASONS_STEP_TYPE_NAME &&
      step.type.title.toLowerCase().includes("reason") &&
      newStepType.title === MULTIPLE_CHOICE_STEP_TYPE_NAME;

    const isMultipleChoiceToReturnReason =
      step.type.title === MULTIPLE_CHOICE_STEP_TYPE_NAME &&
      newStepType.title !== AI_RETURN_REASONS_STEP_TYPE_NAME &&
      newStepType.title.toLowerCase().includes("reason");

    if (isReturnReasonToMultipleChoice || isMultipleChoiceToReturnReason) {
      const currentState: MultipleChoiceState = step.state;
      const newState: MultipleChoiceState = {
        ...currentState,
        reason: isMultipleChoiceToReturnReason,
      };
      const newStep = new TypedStep(newStepType, newState);
      onStepChange(stepId, newStep);
    } else {
      let updatedStep = step;
      for (const downstream of step.downstream()) {
        if (downstream.id) {
          updatedStep = removeStepConnection(updatedStep, downstream.id);
        }
      }

      const newStep = new TypedStep(newStepType, newStepType.empty);
      onStepChange(stepId, newStep);
    }
  });

  // Reset editing state when step changes
  useEffect(() => {
    setIsEditingTitle(false);
    setEditTitle("");
  }, [step]);

  if (!shouldRender || !step || !stepId) {
    return null;
  }

  const detailsComponent = step.details({
    stepSelect: stepConnectionUI,
    setStep,
    flowType,
    steps,
    setStepWithId: onStepChange,
  });

  return (
    <div className={styles.rightPanel} data-visible={visible}>
      <FormSidePanel flex={1} overflowY="auto" side="right">
        <Flex dir="column" px="3xl" py="xl">
          <Flex dir="column" gap="3xl">
            {/* Header */}
            <Flex justify="space-between" py="sm">
              <Flex align="center" gap="sm" style={{ flex: 1 }}>
                {isEditingTitle ? (
                  <Flex align="center" gap="sm" style={{ flex: 1 }}>
                    <RedoTextInput
                      placeholder={step.type.title}
                      setValue={setEditTitle}
                      value={editTitle}
                    />
                    <RedoButton
                      IconLeading={XCloseIcon}
                      onClick={() => setIsEditingTitle(false)}
                    />
                    <RedoButton
                      IconLeading={CheckIcon}
                      onClick={() => {
                        handleTitleChange(editTitle.trim());
                        setIsEditingTitle(false);
                      }}
                    />
                  </Flex>
                ) : (
                  <Flex align="center" grow={1} justify="space-between">
                    <Flex align="center">
                      <Text fontSize="sm" fontWeight="semibold">
                        {step.sidebarTitle()}
                      </Text>
                      {step.sidebarTitle() !== "New step" && (
                        <RedoButton
                          IconLeading={EditPencilIcon}
                          onClick={() => {
                            setEditTitle(step.sidebarTitle());
                            setIsEditingTitle(true);
                          }}
                        />
                      )}
                    </Flex>
                    {stepId !== "start" && (
                      <RedoButton
                        IconLeading={TrashIcon}
                        onClick={handleDeleteClick}
                        theme="destructive"
                      />
                    )}
                  </Flex>
                )}
              </Flex>
              {isEditingTitle ? null : (
                <RedoButton IconLeading={XCloseIcon} onClick={onClose} />
              )}
            </Flex>

            {stepId !== "start" && (
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Type
                </Text>
                <RedoSingleSelectDropdownInput<StepType<any, any>>
                  options={stepOptions.map((stepType) => ({
                    type: "text" as const,
                    id: stepType.title,
                    text: stepType.title,
                    value: stepType,
                  }))}
                  optionSelected={(args) => {
                    handleStepTypeChange(args.item.value);
                  }}
                  placeholder="Select step type"
                  selectedItem={stepOptions
                    .map((stepType) => ({
                      type: "text" as const,
                      id: stepType.title,
                      text: stepType.title,
                      value: stepType,
                    }))
                    .find((item) => item.text === step?.type.title)}
                  size="xs"
                />
              </Flex>
            )}

            <Divider />

            {/* Step editing form */}
            <Flex dir="column" gap="xl">
              {detailsComponent}
            </Flex>

            {/* Validation status */}
            {!step.valid(flowType) && (
              <Flex
                style={{
                  padding: "12px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                }}
              >
                <Text color="#dc2626" fontSize="sm">
                  This step has validation errors that must be fixed before the
                  flow can be published.
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </FormSidePanel>
    </div>
  );
});
